import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  imageUrl?: string;
  structuredData?: Record<string, any>;
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  canonicalUrl,
  ogType = 'website',
  imageUrl = '/assets/branding/finsahaj-logo.webp',
  structuredData,
  noindex = false
}: SEOProps) {
  useEffect(() => {
    // 1. Update Title exactly once
    const finalTitle = title;
    document.title = finalTitle;

    // Helper to strictly manage a single tag in the head
    const updateOrCreateMeta = (selector: string, attrName: string, attrValue: string, contentName: string, contentValue: string) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute(contentName, contentValue);
    };

    const updateOrCreateLink = (rel: string, href: string) => {
      let tag = document.head.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // 2. Update Meta Description
    updateOrCreateMeta('meta[name="description"]', 'name', 'description', 'content', description);

 
    // Helper for absolute URL
    const getAbsoluteUrl = (path: string) => {
      if (!path) return '';
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
    };
    const absoluteImageUrl = getAbsoluteUrl(imageUrl);
    const finalCanonical = canonicalUrl || window.location.href.split('?')[0].split('#')[0];

    // 2.5 Update Robots
    updateOrCreateMeta('meta[name="robots"]','name','robots','content',noindex ? 'noindex, follow' : 'index, follow');
    updateOrCreateMeta('meta[name="googlebot"]','name','googlebot','content',noindex ? 'noindex, follow' : 'index, follow');

    // 3. Update Canonical
    updateOrCreateLink('canonical', finalCanonical);

    // 4. Update OpenGraph Tags
    updateOrCreateMeta('meta[property="og:title"]', 'property', 'og:title', 'content', finalTitle);
    updateOrCreateMeta('meta[property="og:description"]', 'property', 'og:description', 'content', description);
    updateOrCreateMeta('meta[property="og:type"]', 'property', 'og:type', 'content', ogType);
    updateOrCreateMeta('meta[property="og:url"]', 'property', 'og:url', 'content', finalCanonical);
    updateOrCreateMeta('meta[property="og:image"]', 'property', 'og:image', 'content', absoluteImageUrl);
    updateOrCreateMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'content', 'Finsahaj');

    // 5. Update Twitter Tags
    updateOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'content', 'summary_large_image');
    updateOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', 'content', finalTitle);
    updateOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', 'content', description);
    updateOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', 'content', absoluteImageUrl);

    // Helper to process Organizaton Schema dynamics
    const processOrganizationSchema = (data: any): any => {
      if (!data || typeof data !== 'object') return data;
      if (Array.isArray(data)) return data.map(item => processOrganizationSchema(item));
      const clone = { ...data };
      if (clone['@type'] === 'Organization') {
        clone.name = 'Finsahaj';
        const originUrl = window.location.origin;
        const logoUrl = `${originUrl}/assets/branding/finsahaj-logo.webp`;
        if (clone.logo && typeof clone.logo === 'object') {
          clone.logo = { ...clone.logo, '@type': 'ImageObject', 'url': logoUrl };
        } else {
          clone.logo = { '@type': 'ImageObject', 'url': logoUrl };
        }
      }
      for (const key in clone) {
        if (Object.prototype.hasOwnProperty.call(clone, key)) {
          clone[key] = processOrganizationSchema(clone[key]);
        }
      }
      return clone;
    };

    // 6. Inject Structured Data (JSON-LD) exactly once
    const scriptId = 'json-ld-structured-data';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;
    
    if (scriptTag) {
      scriptTag.remove();
    }

    if (structuredData) {
      const processedData = processOrganizationSchema(structuredData);
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      scriptTag.text = JSON.stringify(processedData);
      document.head.appendChild(scriptTag);
    }

    return () => {
      // Optional cleanup on unmount, but often we want the metadata to persist until the next SEO component mounts.
      // We will only cleanup the JSON-LD to prevent stale data. The meta tags will just be overwritten by the next page.
      const currentScript = document.getElementById(scriptId);
      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [title, description, canonicalUrl, ogType, imageUrl, structuredData, noindex]);

  return null;
}
